import { useEffect, useState } from "react";
import useSWR from "swr";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { MessageCircle, Send, ShieldCheck, ExternalLink, History as HistoryIcon, Eye } from "lucide-react";

const fetcher = (url) => http.get(url).then((r) => r.data);

/** Render WhatsApp-style markdown (*bold*, _italic_) as safe React nodes. */
function renderWA(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const parseInline = (line) => {
    // *bold* → <strong>, _italic_ → <em>. Split on either.
    const tokens = line.split(/(\*[^*\n]+\*|_[^_\n]+_)/g).filter(Boolean);
    return tokens.map((t, i) => {
      if (t.startsWith("*") && t.endsWith("*") && t.length > 2)
        return <strong key={i}>{t.slice(1, -1)}</strong>;
      if (t.startsWith("_") && t.endsWith("_") && t.length > 2)
        return <em key={i}>{t.slice(1, -1)}</em>;
      return <span key={i}>{t}</span>;
    });
  };
  return lines.map((line, idx) => (
    <div key={idx} className="min-h-[1.15em]">
      {parseInline(line)}
    </div>
  ));
}

export default function Pengaturan() {
  const { data: settings, mutate } = useSWR("/settings", fetcher);
  const { data: logs, mutate: mutLogs } = useSWR("/wa/logs", fetcher);

  const [form, setForm] = useState({
    whatsapp_number: "",
    fonnte_token: "",
    daily_summary_time: "08:00",
    timezone: "Asia/Jakarta",
    reminders_enabled: true,
    daily_summary_enabled: true,
  });
  const [testMsg, setTestMsg] = useState("Halo dari AsetKu 👋");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (settings) {
      setForm({
        whatsapp_number: settings.whatsapp_number || "",
        fonnte_token: settings.fonnte_token || "",
        daily_summary_time: settings.daily_summary_time || "08:00",
        timezone: settings.timezone || "Asia/Jakarta",
        reminders_enabled: settings.reminders_enabled ?? true,
        daily_summary_enabled: settings.daily_summary_enabled ?? true,
      });
    }
  }, [settings]);

  const save = async () => {
    setBusy(true);
    try {
      await http.put("/settings", form);
      toast.success("Pengaturan tersimpan");
      mutate();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (!form.whatsapp_number) {
      toast.error("Isi nomor WhatsApp dulu");
      return;
    }
    setBusy(true);
    try {
      const { data } = await http.post("/wa/test", { message: testMsg });
      if (data.mocked) toast.warning("Terkirim sebagai MOCK — belum ada Fonnte token");
      else if (data.ok) toast.success("Pesan berhasil dikirim ke WhatsApp");
      else toast.error("Gagal mengirim. Cek token Fonnte & nomor.");
      mutLogs();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const previewSummary = async () => {
    try {
      const { data } = await http.get("/wa/preview?kind=summary");
      setPreview(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-8">
      <header>
        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Pengaturan</div>
        <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
          Integrasi WhatsApp
        </h1>
        <p className="mt-1 text-slate-600">
          Hubungkan akun WhatsApp Anda melalui Fonnte untuk menerima pengingat jadwal otomatis.
        </p>
      </header>

      <Card className="p-6 space-y-5" data-testid="wa-settings-card">
        <div className="flex items-center gap-3 pb-2 border-b border-slate-100">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center">
            <MessageCircle className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display text-lg font-medium text-slate-900">WhatsApp Gateway (Fonnte)</h3>
            <p className="text-xs text-slate-500">
              Daftar di{" "}
              <a href="https://fonnte.com" target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-0.5">
                fonnte.com <ExternalLink className="w-3 h-3" />
              </a>{" "}
              → buat device → salin token API.
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Nomor WhatsApp Anda</Label>
            <Input
              value={form.whatsapp_number}
              onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
              placeholder="+6281234567890"
              data-testid="wa-number-input"
            />
            <p className="mt-1 text-xs text-slate-500">Termasuk kode negara. Contoh: +6281234567890</p>
          </div>
          <div>
            <Label>Token API Fonnte</Label>
            <Input
              type="text"
              value={form.fonnte_token}
              onChange={(e) => setForm({ ...form, fonnte_token: e.target.value })}
              placeholder={settings?.has_fonnte_token ? "Token tersimpan — isi baru untuk mengganti" : "Tempel token Fonnte di sini"}
              data-testid="wa-token-input"
            />
            <p className="mt-1 text-xs text-slate-500 inline-flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Token disimpan aman di server. Kosongkan bila tidak ingin mengganti.
            </p>
          </div>
          <div>
            <Label>Jam ringkasan pagi</Label>
            <Input
              type="time"
              value={form.daily_summary_time}
              onChange={(e) => setForm({ ...form, daily_summary_time: e.target.value })}
              data-testid="wa-summary-time"
            />
          </div>
          <div>
            <Label>Zona waktu</Label>
            <Input
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              placeholder="Asia/Jakarta"
              data-testid="wa-timezone-input"
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
            <div>
              <div className="text-sm font-medium text-slate-800">Ringkasan pagi</div>
              <div className="text-xs text-slate-500">Daftar semua jadwal hari ini</div>
            </div>
            <Switch
              checked={form.daily_summary_enabled}
              onCheckedChange={(v) => setForm({ ...form, daily_summary_enabled: v })}
              data-testid="wa-summary-switch"
            />
          </div>
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
            <div>
              <div className="text-sm font-medium text-slate-800">Pengingat per kegiatan</div>
              <div className="text-xs text-slate-500">Kirim menit sebelum jadwal</div>
            </div>
            <Switch
              checked={form.reminders_enabled}
              onCheckedChange={(v) => setForm({ ...form, reminders_enabled: v })}
              data-testid="wa-reminders-switch"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="wa-save-button">
            Simpan pengaturan
          </Button>
          <Button onClick={previewSummary} variant="outline" className="border-slate-200 gap-2" data-testid="wa-preview-button">
            <Eye className="w-4 h-4" /> Pratinjau ringkasan
          </Button>
          <div className="flex-1 flex gap-2">
            <Input
              value={testMsg}
              onChange={(e) => setTestMsg(e.target.value)}
              placeholder="Pesan uji"
              data-testid="wa-test-message"
            />
            <Button
              onClick={sendTest}
              disabled={busy}
              variant="outline"
              className="border-slate-200 gap-2"
              data-testid="wa-test-send-button"
            >
              <Send className="w-4 h-4" /> Kirim uji
            </Button>
          </div>
        </div>
      </Card>

      {preview !== null && (
        <Card className="p-6" data-testid="wa-preview-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-slate-400" />
              <h3 className="font-display text-lg font-medium text-slate-900">Pratinjau pesan (ringkasan hari ini)</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setPreview(null)} className="text-slate-500">Tutup</Button>
          </div>
          <div className="rounded-lg bg-[#DCF8C6] p-4 text-sm text-slate-900 leading-relaxed border border-emerald-200 max-w-xl" data-testid="wa-preview-body">
            {renderWA(preview)}
          </div>
          <p className="mt-2 text-xs text-slate-500">*Bold* dan _italic_ WhatsApp otomatis diterapkan. Preview di atas mensimulasikan tampilan chat.</p>
        </Card>
      )}

      <Card className="p-6" data-testid="wa-logs-card">
        <div className="flex items-center gap-2 mb-3">
          <HistoryIcon className="w-4 h-4 text-slate-400" />
          <h3 className="font-display text-lg font-medium text-slate-900">Log pengiriman terakhir</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {(logs || []).length === 0 && (
            <div className="text-sm text-slate-500 py-6">Belum ada log pengiriman.</div>
          )}
          {(logs || []).slice(0, 15).map((l) => (
            <div key={l.id} className="py-3 flex items-start gap-3">
              <span
                className={`mt-1 inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                  l.status === "sent"
                    ? "bg-emerald-50 text-emerald-700"
                    : l.status === "mocked"
                    ? "bg-amber-50 text-amber-700"
                    : "bg-rose-50 text-rose-700"
                }`}
              >
                {l.status}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{l.message}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {new Date(l.sent_at).toLocaleString("id-ID")} — {l.kind} → {l.target}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
